import { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";

// Friendly "For kids, click here" bubble on the main start page, pointing at the Academy button.
// Large screens: an arrow under the Academy button. Phones/tablets (Academy sits inside the menu): a small bubble that links straight to the academy.
// Fades in after a moment on every visit, hides once the page is scrolled; a click or ✕ hides it for this visit only.
const TXT = { en: { hint: "For kids, click here!", menu: "For kids: open the menu!", academy: "Now tap Academy!", close: "Hide" }, ar: { hint: "للأطفال، اضغط هنا!", menu: "للأطفال: افتح القائمة!", academy: "الآن اضغط على الأكاديمية!", close: "إخفاء" } };
const CSS = `
@keyframes kh-in { from { opacity: 0; transform: translateY(-6px) scale(.96); } to { opacity: 1; transform: none; } }
@keyframes kh-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.kh-in { animation: kh-in .5s ease-out both; }
.kh-bob { animation: kh-bob 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .kh-in, .kh-bob { animation: none; } }
`;


export function useKidsHint() {
  const [show, setShow] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 1200);
    const onScroll = () => setScrolled(window.scrollY > 160);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); };
  }, []);
  const dismiss = () => { setClosed(true); setShow(false); };
  return { visible: show && !scrolled && !closed, dismiss };
}

function Arrow({ className = "" }) {  // hand-drawn curved arrow pointing up
  return (
    <svg viewBox="0 0 40 46" width="34" height="40" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M30 44 C 10 38, 6 22, 18 6" />
      <path d="M9 12 L 18 5 L 22 16" />
    </svg>
  );
}

function Close({ onClick, label }) {
  return <button type="button" onClick={onClick} aria-label={label} data-testid="kids-hint-close" className="ms-1 -me-1 w-6 h-6 grid place-items-center rounded-full text-white/80 hover:text-white hover:bg-white/15 text-xs leading-none">✕</button>;
}

// Under the desktop Academy button (its wrapper must be `relative`).
export function KidsHintDesktop({ hint }) {
  const { lang } = useApp();
  const t = TXT[lang] || TXT.en;
  if (!hint.visible) return null;
  return (
    <div className="absolute top-full start-1/2 mt-1 z-50 kh-in pointer-events-none" data-testid="kids-hint">
      <style>{CSS}</style>
      <div className="kh-bob flex flex-col items-start">
        <Arrow className="text-[#E8590C] ms-1 rtl:-scale-x-100" />
        <div className="pointer-events-auto -mt-1 ms-5 flex items-center gap-1 rounded-2xl bg-[#E8590C] text-white ps-3.5 pe-2 py-2 text-sm font-bold whitespace-nowrap shadow-lg shadow-[#E8590C]/30">
          <span aria-hidden="true">👋</span> {t.hint}
          <Close onClick={hint.dismiss} label={t.close} />
        </div>
      </div>
    </div>
  );
}

// Phones/tablets, step 1 (menu closed): a bubble under the header with an arrow up to the menu button (tapping the bubble opens the menu too).
export function KidsHintMobile({ hint, onOpen }) {
  const { lang } = useApp();
  const t = TXT[lang] || TXT.en;
  if (!hint.visible) return null;
  return (
    <div className="lg:hidden absolute top-full end-2 -mt-1 z-50 kh-in pointer-events-none" data-testid="kids-hint-mobile">
      <style>{CSS}</style>
      <div className="kh-bob flex flex-col items-end">
        <Arrow className="text-[#E8590C] me-1 -scale-x-100 rtl:scale-x-100" />
        <div className="pointer-events-auto -mt-1 flex items-center gap-1 rounded-2xl bg-[#E8590C] text-white ps-3 pe-1.5 py-1.5 text-sm font-bold shadow-lg shadow-[#E8590C]/30">
          <button type="button" onClick={onOpen} data-testid="kids-hint-open-menu" className="inline-flex items-center gap-1.5"><span aria-hidden="true">👋</span> {t.menu}</button>
          <Close onClick={hint.dismiss} label={t.close} />
        </div>
      </div>
    </div>
  );
}

// Phones/tablets, step 2 (menu open): under the Academy button in the menu, an arrow up to it.
export function KidsHintInMenu({ hint }) {
  const { lang } = useApp();
  const t = TXT[lang] || TXT.en;
  if (!hint.visible) return null;
  return (
    <div className="flex justify-center kh-in pointer-events-none" data-testid="kids-hint-menu">
      <style>{CSS}</style>
      <div className="kh-bob flex flex-col items-center">
        <Arrow className="text-[#E8590C] rtl:-scale-x-100" />
        <div className="pointer-events-auto -mt-1 flex items-center gap-1 rounded-2xl bg-[#E8590C] text-white ps-3 pe-1.5 py-1.5 text-sm font-bold shadow-lg shadow-[#E8590C]/30">
          <span aria-hidden="true">👋</span> {t.academy}
          <Close onClick={hint.dismiss} label={t.close} />
        </div>
      </div>
    </div>
  );
}
